import { Col, Row } from 'antd';
import Alerts from 'common/alerts/containers/alert-container';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, injectIntl, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Icon } from 'ui';
import { alertSeverities, referenceTypes, tagTypes } from 'utils/app-configs';
import moment from 'utils/cops-moment';
import { getDuration } from 'utils/helpers';
import { blockConfig } from '../../../config';
import Badge from './block-badge';
import BlockDetailActions from './block-detail-actions';
import Tag from './block-tag';

const LeftCol = styled(Col)`
  border-right: 1px solid #d9e0e6;
`;

const RightCol = styled(Col)`
  padding-left: 18px;
`;

const StyledLabel = styled.p`
  font-size: 12px;
  color: #64747c;
`;

const StyledText = styled.p`
  font-size: 14px;
`;

const TagWrapper = styled.p`
  font-size: 14px;
  >:first-child {
    margin-right: 5px;
  }
`;

const InnerRow = styled(Row)`
  margin-top: ${p => p.marginTop || 8}px;
`;

const InnerCol = styled(Col)`
  line-height: 16px;
`;

const StyledLink = styled.a`
  font-size: 14px;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  &:active {
    color: #0000EE !important;
  }
  &:focus {
    color: #0000EE !important;
  }
`;

const StyledTagText = styled.span`
  margin-left: 5px;
`;

const LongText = styled.p`
  max-width: 630px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 3px;
`;

export const renderLockTag = (lock, isAssignedPlanner, hasError, isDraft, isPublishing) => {
  if (!hasError && isPublishing) {
    return null;
  }

  if (lock) {
    if (!isAssignedPlanner) {
      return (
        <Tag
          color={blockConfig.lock.color}
          tagIcon={blockConfig.lock.draftIcon}
          customIcon={true}
        />
      );
    }
    if (hasError) {
      return (
        <Tag
          color={blockConfig.isDraft.errorColor}
          tagIcon={blockConfig.isDraft.icon}
        />
      );
    }
    return null;
  }
  if (isDraft) {
    if (!isAssignedPlanner) {
      return (
        <Tag
          color={blockConfig.lock.color}
          tagIcon={blockConfig.lock.draftIcon}
          customIcon={true}
        />
      );
    }
    if (hasError) {
      return (
        <Tag
          color={blockConfig.isDraft.errorColor}
          tagIcon={blockConfig.isDraft.icon}
        />
      );
    }
    return (
      <Tag
        color={blockConfig.isDraft.color}
        tagIcon={blockConfig.isDraft.icon}
      />
    );
  }
  return null;
};

export const getStatusLabelKey = (hasError, isPublishing) => {
  if (hasError) {
    return 'TO_BE_CORRECTED_BY';
  } else if (isPublishing) {
    return 'PUBLISHING_TO_SAP_BY';
  }
  return 'NOT_PUBLISHED_BY';
};

const WarningRow = styled.div`
  width: calc(100% + 24px);
  margin-left: -12px;
  margin-top: -10px;
  border: 3px solid ${p => p.theme.colors.theme};
  background: rgba(0, 152, 204, 0.2);
  padding: 0 12px;
  margin-bottom: 10px;
  font-weight: 600;
  i  {
    margin-right: 10px;
  }
`;

const BlockDetails = ({
  block,
  isPopoverVisible,
  setIsPopoverVisible,
  publishToSAP,
  splitBlock,
  deletePlanning,
  markDateConfirmed,
  isDoingAction,
  fetchBlockDataSuccess,
  updateBlockId,
  updateBlockOperationInfo,
  isBlockLoading,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
}) => {
  const intl = useIntl();
  if (!isPopoverVisible) {
    return null;
  }

  const orderStatusTag = block.tags?.find(t => t.type === tagTypes.orderStatus);
  const orderTypeTag = block.tags?.find(t => t.type === tagTypes.orderType);
  const isReservation = block.type === blockConfig.types.reservation;
  const isAbsence = block.type === blockConfig.types.absence;
  const isRouteOptimisation = block?.type === blockConfig.types.placeholder;

  const renderReferenceBlock = (ref) => {
    if (ref.type === referenceTypes.equipment) {
      return (
        <InnerCol span={24}>
          <StyledLabel><FormattedMessage id='planningBoard.blocks.EQUIPMENT' /></StyledLabel>
          <StyledLink
            href={`/${ref.url}?context=${block?.objectId}`}
          >
            {ref.description}
          </StyledLink>
        </InnerCol>
      );
    } else if (ref.type === referenceTypes.installedAt) {
      return (
        <InnerCol span={24}>
          <StyledLabel><FormattedMessage id='planningBoard.blocks.INSTALLEDAT' /></StyledLabel>
          <StyledLink
            href={`/${ref.url}?context=${block?.objectId}`}
          >
            {ref.description}
          </StyledLink>
        </InnerCol>
      );
    } else if (ref.type === referenceTypes.functionallocation) {
      return (
        <InnerCol span={24}>
          <StyledLabel>
            <FormattedMessage id='planningBoard.blocks.FUNCTIONALLOCATION' />
          </StyledLabel>
          <StyledLink
            href={`/${ref.url}?context=${block?.objectId}`}
          >
            {ref.description}
          </StyledLink>
        </InnerCol>
      );
    } else if (ref.type === referenceTypes.customer) {
      return (
        <InnerCol span={24}>
          <StyledLabel><FormattedMessage id='planningBoard.blocks.CUSTOMER' /></StyledLabel>
          <StyledLink
            href={`/${ref.url}?context=${block?.objectId}`}
          >
            {ref.description}
          </StyledLink>
        </InnerCol>
      );
    } else if (ref.type === referenceTypes.order) {
      return (
        <InnerCol span={24}>
          <StyledLabel><FormattedMessage id='planningBoard.blocks.ORDER' /></StyledLabel>
          <StyledLink
            href={`/${ref.url}?context=${block?.objectId}`}
          >
            {ref.description}
          </StyledLink>
        </InnerCol>
      );
    }
    return (
      <InnerCol span={24}>
        <StyledLabel>{ref.type.toUpperCase()}</StyledLabel>
        <StyledLink
          href={`/${ref.url}?context=${block?.objectId}`}
        >
          {ref.description}
        </StyledLink>
      </InnerCol>
    );
  };

  const getNameAndTime = () => block.lock && block.lock.lockedReason !== 'readonly' ?
    `${block?.isAssignedPlanner ?
      intl.formatMessage({ id: 'planningBoard.blocks.YOU' }) :
      block.lock.lockedByName} ${block.lock.lockedOn ?
        `(${moment(block.lock.lockedOn).fromNow()})` :
        ''}` :
    `${block?.isAssignedPlanner ?
      intl.formatMessage({ id: 'planningBoard.blocks.YOU' }) :
      block?.assignedPlannerName} ${block.draftSince ?
        `(${moment(block.draftSince).fromNow()})` :
        ''}`;

  const blockDetailActions =
    <BlockDetailActions
      actions={block.actions}
      setIsPopoverVisible={setIsPopoverVisible}
      planningId={block.id}
      publishToSAP={publishToSAP}
      splitBlock={splitBlock}
      markDateConfirmed={markDateConfirmed}
      isDoingAction={isDoingAction}
      isReservation={isReservation}
      isRouteOptimisation={isRouteOptimisation}
      isAbsence={isAbsence}
      deletePlanning={deletePlanning}
      block={block}
      isPopoverVisible={isPopoverVisible}
      fetchBlockDataSuccess={fetchBlockDataSuccess}
      updateBlockId={updateBlockId}
      updateBlockOperationInfo={updateBlockOperationInfo}
      isAssignedPlanner={block.isAssignedPlanner}
      plannerInfo={getNameAndTime()}
      markDateUnconfirmed={markDateUnconfirmed}
      restoreBackup={restoreBackup}
      recalculateTravelTime={recalculateTravelTime}
    />;

  const renderLock = (span, row) => {
    const hasError =
      block?.operationInfo?.errors?.length ||
      block?.operationInfo?.isTimedOut ||
      block?.operationInfo?.isStale;

    const children = <InnerCol span={span}>
      <StyledLabel>
        <FormattedMessage
          id={`planningBoard.blocks.${getStatusLabelKey(hasError, isBlockLoading)}`}
        />
      </StyledLabel>
      <TagWrapper>
        {renderLockTag(
          block?.lock,
          block?.isAssignedPlanner,
          hasError,
          block?.isDraft,
          isBlockLoading,
        )}
        <span>
          {getNameAndTime()}
        </span>
      </TagWrapper>
    </InnerCol>;
    if (hasError || isBlockLoading || block?.isDraft) {
      if (row) {
        return <Row>{children}</Row>;
      }
      return children;
    }
    return null;
  };

  if (isRouteOptimisation) {
    return (
      <>
        {blockDetailActions}
        <Row>
          <InnerCol span={24}>
            <StyledLabel>
              <>
                <FormattedMessage id='planningBoard.routeOptimisation.TITLE' />
                <StyledIcon type='info-circle' />
              </>
            </StyledLabel>
            {/* eslint-disable-next-line max-len*/}
            <LongText><FormattedMessage id='planningBoard.routeOptimisation.EXPLANATION' /></LongText>

          </InnerCol>
        </Row>
        <InnerRow>
          <InnerCol span={8}>
            <StyledLabel>
              <FormattedMessage id='planningBoard.routeOptimisation.REQUESTED_BY' />
            </StyledLabel>
            {block?.requestedBy ?
              <StyledText>
                {block?.requestedBy}
              </StyledText> :
              null
            }
          </InnerCol>
          <InnerCol span={8}>
            <StyledLabel>
              <FormattedMessage id='planningBoard.routeOptimisation.REQUESTED_ON' />
            </StyledLabel>
            {block?.requestedOn ?
              <StyledText>
                <FormattedDate value={block?.requestedOn} /> {' '}
                <FormattedTime value={block?.requestedOn} />
              </StyledText> :
              null
            }
          </InnerCol>
          <InnerCol span={8}>
            <StyledLabel>
              <FormattedMessage id='planningBoard.routeOptimisation.LASTUPDATED' />
            </StyledLabel>
            {block?.lastUpdated ?
              <StyledText>
                <FormattedDate value={block?.lastUpdated} /> {' '}
                <FormattedTime value={block?.lastUpdated} />
              </StyledText> :
              null
            }
          </InnerCol>
        </InnerRow>
      </>
    );
  }

  if (isReservation || isAbsence) {
    return (
      <>
        {blockDetailActions}
        {block?.outOfScope ?
          <WarningRow>
            <Icon type='circle-info' />
            <FormattedMessage id='planning.OUT_OF_SCOPE' />
          </WarningRow> :
          null
        }
        <Row>
          <InnerCol span={12}>
            <StyledLabel><FormattedMessage id='planningBoard.blocks.START_DATE' /></StyledLabel>
            {block?.start ?
              <StyledText>
                {moment(block?.start).format('dddd')}{' '}<FormattedDate value={block?.start} />
              </StyledText> :
              null
            }
          </InnerCol>
          {renderLock(12, false)}
        </Row>
        <InnerRow>
          <InnerCol span={12}>
            <StyledLabel><FormattedMessage id='planningBoard.blocks.START_TIME' /></StyledLabel>
            <StyledText><FormattedTime value={block?.start} /></StyledText>
          </InnerCol>
          <InnerCol span={12}>
            <StyledLabel><FormattedMessage id='planningBoard.blocks.DURATION' /></StyledLabel>
            {block?.duration ?
              <StyledText>
                {getDuration(block?.duration)}
              </StyledText> :
              null
            }
          </InnerCol>
        </InnerRow>
        <InnerRow>
          <InnerCol span={24}>
            <StyledLabel><FormattedMessage id='planningBoard.blocks.DESCRIPTION' /></StyledLabel>
            <StyledText>{block?.line1}</StyledText>
          </InnerCol>
        </InnerRow>
      </>
    );
  }

  return (
    <>
      {blockDetailActions}
      {block?.outOfScope ?
        <WarningRow>
          <Icon type='circle-info' />
          <FormattedMessage id='planning.OUT_OF_SCOPE' />
        </WarningRow> :
        null
      }
      <Row>
        <LeftCol span={14}>
          <Row>
            <InnerCol span={24}>
              <StyledLabel><FormattedMessage id='planningBoard.blocks.START_DATE' /></StyledLabel>
              {block?.start ?
                <StyledText data-test-id='block-l2-startdate' >
                  {moment(block?.start).format('dddd')}{' '}<FormattedDate value={block?.start} />
                </StyledText> :
                null
              }
            </InnerCol>
          </Row>
          <InnerRow>
            <InnerCol span={8}>
              <StyledLabel><FormattedMessage id='planningBoard.blocks.START_TIME' /></StyledLabel>
              <StyledText data-test-id='block-l2-start-time'><FormattedTime
                value={block?.start}
              /></StyledText>
            </InnerCol>
            <InnerCol span={8}>
              <StyledLabel>
                <FormattedMessage id='planningBoard.blocks.DURATION_LABOUR' />
              </StyledLabel>
              {block?.subBlocks?.find(s => s.activityType === blockConfig.activityTypes.labour) ?
                <StyledText data-test-id='block-l2-labour-duration'>
                  {getDuration(block?.subBlocks?.find(s =>
                    s.activityType === blockConfig.activityTypes.labour).duration)}
                </StyledText> :
                null
              }
            </InnerCol>
            <InnerCol span={8}>
              <StyledLabel>
                <FormattedMessage id='planningBoard.blocks.DURATION_TOTAL' />
              </StyledLabel>
              {block?.duration ?
                <StyledText>
                  {getDuration(block?.duration)}
                </StyledText> :
                null
              }
            </InnerCol>
          </InnerRow>
          {block?.references?.filter(r => r.type !== referenceTypes.customer)?.map(t =>
            <InnerRow key={t.type}>
              {renderReferenceBlock(t)}
            </InnerRow>)}
        </LeftCol>
        <RightCol span={10}>
          {renderLock(24, true)}
          <InnerRow marginTop={0}>
            <InnerCol span={24}>
              <StyledLabel><FormattedMessage id='planningBoard.blocks.ALERTS' /></StyledLabel>
              <StyledText>
                <Alerts
                  blockAlerts={block?.alert?.alerts?.map(a => ({
                    ...a,
                    severity: blockConfig.blockSeverities[a.level?.toUpperCase()],
                    name: a.description,
                  })) || []}
                  needsActions={false}
                  maxVisibleAlerts={block?.alert?.alerts?.length}
                  showFullDescription={false}
                  tagOnly={true}
                  needsTitle={false}
                  fontSize={14}
                />

              </StyledText>
            </InnerCol>
          </InnerRow>
          <InnerRow>
            <InnerCol span={24}>
              <StyledLabel><FormattedMessage id='planningBoard.blocks.ORDER_TYPE' /></StyledLabel>
              <StyledText>
                {orderTypeTag ?
                  <>
                    <Tag
                      color={alertSeverities[blockConfig.blockSeverities.INFO].colorStatus}
                      text={orderTypeTag.description}
                      shortText={orderTypeTag.shortDescription}
                      showShortText={true}
                    />
                    <StyledTagText>{orderTypeTag.description}</StyledTagText>
                  </> :
                  null
                }
              </StyledText>
            </InnerCol>
          </InnerRow>
          <InnerRow>
            <InnerCol span={24}>
              <StyledLabel><FormattedMessage id='planningBoard.blocks.ORDER_STATUS' /></StyledLabel>
              <StyledText>
                {orderStatusTag ?
                  <>
                    <Tag
                      color={alertSeverities[blockConfig.blockSeverities.INFO].colorStatus}
                      text={orderStatusTag.description}
                      shortText={orderStatusTag.shortDescription}
                      showShortText={true}
                    />
                    <StyledTagText>{orderStatusTag.description}</StyledTagText>
                  </> :
                  null
                }
              </StyledText>
            </InnerCol>
          </InnerRow>
          {block?.badges?.length ?
            <InnerRow>
              <InnerCol span={24}>
                <StyledLabel>
                  <FormattedMessage id='planningBoard.blocks.ADDITIONAL_INFO' />
                </StyledLabel>
                <StyledText>
                  {block.badges?.map((b, i) =>
                    <>
                      <Badge icon={b.icon} iconStyle={b.iconStyle} />
                      <StyledTagText>
                        {b.description}{i + 1 < block?.badges?.length ? '; ' : ''}
                      </StyledTagText>
                    </>,
                  )}
                </StyledText>
              </InnerCol>
            </InnerRow> :
            null}
        </RightCol>
      </Row>
    </>
  );
};

BlockDetails.propTypes = {
  block: PropTypes.object,
  setIsPopoverVisible: PropTypes.func,
  publishToSAP: PropTypes.func,
  markDateConfirmed: PropTypes.func,
  isDoingAction: PropTypes.bool,
  deletePlanning: PropTypes.func,
  isPopoverVisible: PropTypes.bool,
  fetchBlockDataSuccess: PropTypes.func,
  updateBlockId: PropTypes.func,
  updateBlockOperationInfo: PropTypes.func,
  splitBlock: PropTypes.func,
  isBlockLoading: PropTypes.bool,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
};

export default injectIntl(BlockDetails);
